import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-voiceagent');

// Debug flags for AudioPlayer
const AUDIO_PLAYER_DEBUG = false; // Set to true to see detailed AudioPlayer logs
const AUDIO_PLAYER_VERBOSE = false; // Set to true to see very detailed logs (resampling, etc.)

export class AudioPlayer {
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private analyser: AnalyserNode | null = null;
  private initialized: boolean = false;
  private isUserInteracted: boolean = false;
  private onAudioPlayedListeners: Array<(samples: Float32Array) => void> = [];
  private initializationPromise: Promise<void> | null = null;

  constructor() {
    // AudioContext will be initialized on first use or explicitly by calling start()
    // Add click listener to detect user interaction
    this.setupUserInteractionDetection();
  }

  private setupUserInteractionDetection(): void {
    const handleUserInteraction = () => {
      this.isUserInteracted = true;
      if (AUDIO_PLAYER_DEBUG) {
        logger.info('🎵 AudioPlayer: User interaction detected, audio is now allowed');
      }
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
  }

  public addEventListener(event: string, callback: (samples: Float32Array) => void): void {
    switch (event) {
      case "onAudioPlayed":
        this.onAudioPlayedListeners.push(callback);
        break;
      default:
        logger.error("Listener registered for event type: " + JSON.stringify(event) + " which is not supported");
    }
  }

  public async start(): Promise<void> {
    if (!this.isUserInteracted) {
      if (AUDIO_PLAYER_DEBUG) {
        logger.warn('🎵 AudioPlayer: start() called but no user interaction detected yet');
      }
      return;
    }

    if (this.initialized) {
      if (AUDIO_PLAYER_DEBUG) {
        logger.info('🎵 AudioPlayer: Already initialized, skipping start()');
      }
      return;
    }

    // If initialization is already in progress, wait for it to complete
    if (this.initializationPromise) {
      if (AUDIO_PLAYER_DEBUG) {
        logger.info('🎵 AudioPlayer: Initialization already in progress, waiting...');
      }
      await this.initializationPromise;
      return;
    }

    // Start initialization process
    this.initializationPromise = this.initialize();
    await this.initializationPromise;
    this.initializationPromise = null;
  }

  private async initialize(): Promise<void> {
    try {
      // Create AudioContext with fixed 24kHz sample rate for consistency
      this.audioContext = new AudioContext({ sampleRate: 24000 });

      if (this.audioContext.state === 'suspended') {
        if (AUDIO_PLAYER_DEBUG) {
          logger.info('🎵 AudioPlayer: Resuming suspended AudioContext');
        }
        await this.audioContext.resume();
      }

      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 512;

      // Load and create the AudioWorklet processor
      try {
        // Convert worklet code to inline approach for better compatibility
        const workletCode = `
            // Audio sample buffer to minimize reallocations
            class ExpandableBuffer {
                constructor() {
                    // Start with one second's worth of buffered audio capacity before needing to expand
                    this.buffer = new Float32Array(24000);
                    this.readIndex = 0;
                    this.writeIndex = 0;
                    this.underflowedSamples = 0;
                    this.isInitialBuffering = true;
                    this.initialBufferLength = 24000;  // One second
                    this.lastWriteTime = 0;
                }

                logTimeElapsedSinceLastWrite() {
                    const now = Date.now();
                    if (this.lastWriteTime !== 0) {
                        const elapsed = now - this.lastWriteTime;
                        console.log(\`Elapsed time since last audio buffer write: \${elapsed} ms\`);
                    }
                    this.lastWriteTime = now;
                }

                write(samples) {
                    this.logTimeElapsedSinceLastWrite();
                    if (this.writeIndex + samples.length <= this.buffer.length) {
                        // Enough space to append the new samples
                    }
                    else {
                        // Not enough space ...
                        if (samples.length <= this.readIndex) {
                            // ... but we can shift samples to the beginning of the buffer
                            const subarray = this.buffer.subarray(this.readIndex, this.writeIndex);
                            console.log(\`Shifting the audio buffer of length \${subarray.length} by \${this.readIndex}\`);
                            this.buffer.set(subarray);
                        }
                        else {
                            // ... and we need to grow the buffer capacity to make room for more audio
                            const newLength = (samples.length + this.writeIndex - this.readIndex) * 2;
                            const newBuffer = new Float32Array(newLength);
                            console.log(\`Expanding the audio buffer from \${this.buffer.length} to \${newLength}\`);
                            newBuffer.set(this.buffer.subarray(this.readIndex, this.writeIndex));
                            this.buffer = newBuffer;
                        }
                        this.writeIndex -= this.readIndex;
                        this.readIndex = 0;
                    }
                    this.buffer.set(samples, this.writeIndex);
                    this.writeIndex += samples.length;
                    if (this.writeIndex - this.readIndex >= this.initialBufferLength) {
                        // Filled the initial buffer length, so we can start playback with some cushion
                        this.isInitialBuffering = false;
                        console.log("Initial audio buffer filled");
                    }
                }

                read(destination) {
                    let copyLength = 0;
                    if (!this.isInitialBuffering) {
                        // Only start to play audio after we've built up some initial cushion
                        copyLength = Math.min(destination.length, this.writeIndex - this.readIndex);
                    }
                    destination.set(this.buffer.subarray(this.readIndex, this.readIndex + copyLength));
                    this.readIndex += copyLength;
                    if (copyLength > 0 && this.underflowedSamples > 0) {
                        console.log(\`Detected audio buffer underflow of \${this.underflowedSamples} samples\`);
                        this.underflowedSamples = 0;
                    }
                    if (copyLength < destination.length) {
                        // Not enough samples (buffer underflow). Fill the rest with silence.
                        destination.fill(0, copyLength);
                        this.underflowedSamples += destination.length - copyLength;
                    }
                    if (copyLength === 0) {
                        // Ran out of audio, so refill the buffer to the initial length before playing more
                        this.isInitialBuffering = true;
                    }
                }

                clearBuffer() {
                    this.readIndex = 0;
                    this.writeIndex = 0;
                }
            }

            class AudioPlayerProcessor extends AudioWorkletProcessor {
                constructor() {
                    super();
                    this.playbackBuffer = new ExpandableBuffer();
                    this.port.onmessage = (event) => {
                        if (event.data.type === "audio") {
                            this.playbackBuffer.write(event.data.audioData);
                        }
                        else if (event.data.type === "initial-buffer-length") {
                            // Override the current playback initial buffer length
                            const newLength = event.data.bufferLength;
                            this.playbackBuffer.initialBufferLength = newLength;
                            console.log(\`Changed initial audio buffer length to: \${newLength}\`);
                        }
                        else if (event.data.type === "barge-in") {
                            this.playbackBuffer.clearBuffer();
                        }
                    };
                }

                process(inputs, outputs, parameters) {
                    const output = outputs[0][0]; // Assume one output with one channel
                    this.playbackBuffer.read(output);
                    return true; // True to continue processing
                }
            }

            registerProcessor("audio-player-processor", AudioPlayerProcessor);
          `;

        // Create a blob URL for the worklet code
        const workletBlob = new Blob([workletCode], { type: 'application/javascript' });
        const workletUrl = URL.createObjectURL(workletBlob);

        if (AUDIO_PLAYER_DEBUG) {
          logger.info('🎵 AudioPlayer: Loading worklet from inline blob URL:', workletUrl);
        }

        await this.audioContext.audioWorklet.addModule(workletUrl);
        if (AUDIO_PLAYER_DEBUG) {
          logger.info('🎵 AudioPlayer: Worklet module loaded successfully');
        }

        // Clean up the blob URL
        URL.revokeObjectURL(workletUrl);

        // Firefox requires more time for the processor to be registered
        // Try multiple times with increasing delays
        let attempts = 0;
        const maxAttempts = 10;

        while (attempts < maxAttempts) {
          try {
            await new Promise(resolve => setTimeout(resolve, 50 * (attempts + 1))); // 50ms, 100ms, 150ms, etc.

            this.workletNode = new AudioWorkletNode(this.audioContext, "audio-player-processor");
            if (AUDIO_PLAYER_DEBUG) {
              logger.info(`🎵 AudioPlayer: AudioWorkletNode created successfully on attempt ${attempts + 1}`);
            }
            break; // Success, exit the loop
          } catch (error) {
            attempts++;
            if (attempts >= maxAttempts) {
              throw new Error(`Failed to create AudioWorkletNode after ${maxAttempts} attempts: ${error.message}`);
            }
            if (AUDIO_PLAYER_DEBUG) {
              logger.info(`🎵 AudioPlayer: Attempt ${attempts} failed, retrying...`);
            }
          }
        }

        // Connect the worklet nodes
        this.workletNode.connect(this.analyser);
        this.analyser.connect(this.audioContext.destination);
      } catch (error) {
        logger.error('🎵 AudioPlayer: Error loading worklet:', error);
        // Don't throw error, just mark as not initialized so it can be retried
        this.initialized = false;
        this.audioContext = null;
        this.workletNode = null;
        this.analyser = null;
        return;
      }

      // Set up audio monitoring (equivalent to recorderNode in original)
      const recorderNode = this.audioContext.createScriptProcessor(512, 1, 1);
      recorderNode.onaudioprocess = (event) => {
        // Pass the input along as-is
        const inputData = event.inputBuffer.getChannelData(0);
        const outputData = event.outputBuffer.getChannelData(0);
        outputData.set(inputData);
        // Notify listeners that the audio was played
        const samples = new Float32Array(outputData.length);
        samples.set(outputData);
        this.onAudioPlayedListeners.forEach(listener => listener(samples));
      };

      this.maybeOverrideInitialBufferLength();
      this.initialized = true;

      if (AUDIO_PLAYER_DEBUG) {
        logger.info('🎵 AudioPlayer started successfully with AudioWorklet, AudioContext state:', this.audioContext?.state);
      }
    } catch (error) {
      logger.error('🎵 AudioPlayer: Error during initialization:', error);
      this.initialized = false;
      throw error;
    }
  }

  private maybeOverrideInitialBufferLength(): void {
    // Read a user-specified initial buffer length from the URL parameters to help with tinkering
    const params = new URLSearchParams(window.location.search);
    const value = params.get("audioPlayerInitialBufferLength");
    if (value === null) {
      return;  // No override specified
    }
    const bufferLength = parseInt(value);
    if (isNaN(bufferLength)) {
      logger.error("Invalid audioPlayerInitialBufferLength value:", JSON.stringify(value));
      return;
    }
    if (this.workletNode) {
      this.workletNode.port.postMessage({
        type: "initial-buffer-length",
        bufferLength: bufferLength,
      });
    }
  }

  public async playAudio(audioData: Float32Array): Promise<void> {
    if (AUDIO_PLAYER_VERBOSE) {
      logger.info('🎵 AudioPlayer: playAudio called with data length:', audioData?.length);
    }

    if (!audioData || audioData.length === 0) {
      if (AUDIO_PLAYER_DEBUG) {
        logger.warn('🎵 AudioPlayer: Received empty audio data.');
      }
      return;
    }

    if (!this.isUserInteracted) {
      if (AUDIO_PLAYER_DEBUG) {
        logger.warn('🎵 AudioPlayer: Cannot play audio without user interaction');
      }
      return;
    }

    if (!this.initialized) {
      if (AUDIO_PLAYER_DEBUG) {
        logger.info('🎵 AudioPlayer: Not initialized, calling start()');
      }
      await this.start();
    }

    if (!this.initialized || !this.workletNode) {
      logger.error("🎵 AudioPlayer: AudioPlayer not initialized or workletNode not available, retrying initialization...");
      // Reset and try to initialize again
      this.initialized = false;
      this.audioContext = null;
      this.workletNode = null;
      this.analyser = null;
      await this.start();

      // Check again after retry
      if (!this.initialized || !this.workletNode) {
        logger.error("🎵 AudioPlayer: AudioPlayer still not initialized after retry, skipping audio playback");
        return;
      }
    }

    try {
      // Send audio data directly to the worklet (no resampling needed since we use 24kHz context)
      this.workletNode.port.postMessage({
        type: "audio",
        audioData: audioData,
      });

      if (AUDIO_PLAYER_VERBOSE) {
        logger.info('🎵 AudioPlayer: Audio data sent to worklet processor');
      }
    } catch (error) {
      logger.error('🎵 AudioPlayer: Error sending audio to worklet:', error);
    }
  }

  public getSamples(): number[] | null {
    if (!this.initialized || !this.analyser) {
      return null;
    }
    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteTimeDomainData(dataArray);
    return Array.from(dataArray).map(e => e / 128 - 1);
  }

  public getVolume(): number {
    if (!this.initialized || !this.analyser) {
      return 0;
    }
    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteTimeDomainData(dataArray);
    const normSamples = Array.from(dataArray).map(e => e / 128 - 1);
    let sum = 0;
    for (let i = 0; i < normSamples.length; i++) {
      sum += normSamples[i] * normSamples[i];
    }
    return Math.sqrt(sum / normSamples.length);
  }

  public stop(): void {
    if (AUDIO_PLAYER_DEBUG) {
      logger.info('🎵 AudioPlayer: stop() called');
    }

    if (!this.audioContext) return;

    // Stop the worklet if it exists
    if (this.workletNode) {
      try {
        this.workletNode.disconnect();
        if (AUDIO_PLAYER_DEBUG) {
          logger.info("🎵 AudioPlayer: Disconnected worklet node.");
        }
      } catch (e) {
        logger.warn("🎵 AudioPlayer: Error disconnecting worklet node:", e);
      }
    }

    // Stop the analyser if it exists
    if (this.analyser) {
      try {
        this.analyser.disconnect();
        if (AUDIO_PLAYER_DEBUG) {
          logger.info("🎵 AudioPlayer: Disconnected analyser node.");
        }
      } catch (e) {
        logger.warn("🎵 AudioPlayer: Error disconnecting analyser node:", e);
      }
    }

    if (AUDIO_PLAYER_DEBUG) {
      logger.info('🎵 AudioPlayer stopped');
    }
  }

  public bargeIn(): void {
    if (!this.initialized || !this.workletNode) {
      if (AUDIO_PLAYER_DEBUG) {
        logger.info('🎵 AudioPlayer: bargeIn ignored - not initialized or no worklet');
      }
      return;
    }

    try {
      this.workletNode.port.postMessage({
        type: "barge-in",
      });
      if (AUDIO_PLAYER_DEBUG) {
        logger.info("🎵 AudioPlayer: Barge-in message sent to worklet.");
      }
    } catch (error) {
      logger.error("🎵 AudioPlayer: Error sending barge-in to worklet:", error);
    }
  }

  public async close(): Promise<void> {
    if (AUDIO_PLAYER_DEBUG) {
      logger.info('🎵 AudioPlayer: close() called');
    }

    this.stop();

    if (this.audioContext && this.audioContext.state !== 'closed') {
      await this.audioContext.close();
      this.audioContext = null;
      if (AUDIO_PLAYER_DEBUG) {
        logger.info('🎵 AudioPlayer closed, AudioContext released.');
      }
    }

    this.workletNode = null;
    this.analyser = null;
    this.initialized = false;
  }
}
